import React from 'react';
import { Box } from '@mui/material';
import { Nullable, SelectOption } from 'global';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { equipmentConditions, equipmentYears } from 'modules/equipment';
import { FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { AutocompleteValueField, CurrencyInputField, FormInputSelect, InputField, SelectField } from 'components/form';
import { EquipmentActions } from './EquipmentActions';
import {
  FormSectionHeader,
  NotificationAlert,
  ProductsDynamicFields,
  SerialNumberVinInput,
} from '../../../../components';
import { deskingActions, deskingSelectors } from '../../../../../model';
import { dynamicFieldErrorText, equipmentDependentFields } from '../../../../../constants';
import {
  Fact,
  FormSectionProps,
  UserActionsEvents,
  FinanceProgramFormValues,
  DeskingConfigurationBaseOnAccountFormFieldsRequire,
} from '../../../../../types';
import { equipmentMileageFormatter } from '../../../../../lib';
import { useSearchByVin } from 'modules/desking/hooks';

type Props = FormSectionProps &
  Partial<UserActionsEvents> & { formFieldsRequire: Nullable<DeskingConfigurationBaseOnAccountFormFieldsRequire> };

export const Equipment = ({
  sectionName,
  formFieldsRequire,
  handleUpdateOpenStatus,
  refetchProposalProducts,
  handleChangeSelectedProductsConfiguration,
  ...userActionsEvents
}: Props) => {
  const { control, setValue } = useFormContext<FinanceProgramFormValues>();

  const dispatch = useDispatch();

  const isEditMode = useSelector(deskingSelectors.isEditEquipmentStatus);
  const commonProductsConfiguration = useSelector(deskingSelectors.commonProductsConfiguration);
  const isSectionOpen = useSelector((state: any) => deskingSelectors.isSectionOpen(state, sectionName));
  const isSectionCollapsed = useSelector((state: any) => deskingSelectors.isSectionCollapsed(state, sectionName));

  const equipments = useSelector(deskingSelectors.equipment);

  const makeOptions = useSelector(deskingSelectors.makeEquipmentOptions);
  const classOptions = useSelector(deskingSelectors.classEquipmentOptions);
  const equipmentOptions = useSelector(deskingSelectors.equipmentSelectOptions);

  const { setEquipmentInfoFromDecodedValues } = useSearchByVin();

  const handleChangeEquipment = async (equipmentId: string) => {
    dispatch(deskingActions.toggleIsEditEquipmentStatus({ status: false }));

    await refetchProposalProducts();

    const selectedEquipment = equipments?.find(equipment => equipment.equipmentId === equipmentId);

    setValue('equipment.selectedEquipment', equipmentId);

    setValue(`equipment.mileage`, equipmentMileageFormatter(selectedEquipment?.mileage ?? null, 'toView').toString());

    equipmentDependentFields.forEach(field => setValue(`equipment.${field}`, selectedEquipment?.[field] ?? ''));

    dispatch(deskingActions.setCurrentEquipmentData({ id: equipmentId }));
  };

  const determineDynamicFieldError = (fact: Fact) => isEditMode && !commonProductsConfiguration[fact];

  const handleSearchByVinResult = (decodedEquipmentInfo: any) => {
    setEquipmentInfoFromDecodedValues(decodedEquipmentInfo, handleChangeSelectedProductsConfiguration);
  };

  return (
    <Box className="section">
      <FormSectionHeader
        name="Equipment"
        sectionName={sectionName}
        isSectionOpen={isSectionOpen}
        isSectionCollapsed={isSectionCollapsed}
        handleUpdateOpenStatus={handleUpdateOpenStatus}
        extraAction={
          <EquipmentActions
            isEquipmentExist={!!equipments?.length}
            isEditEquipmentFormMode={isEditMode}
            onToggleEquipmentFormMode={status => {
              dispatch(deskingActions.toggleIsEditEquipmentStatus({ status }));
            }}
            {...userActionsEvents}
          />
        }
      />

      {isSectionOpen && (
        <>
          {!equipments?.length ? (
            <NotificationAlert message="You don’t have any equipment yet. Please, press ‘Add Equipment’ button to add one." />
          ) : (
            <Box className="sectionFields">
              <Box className="row">
                <Controller
                  control={control}
                  name="equipment.selectedEquipment"
                  render={({ field }) => (
                    <FormInputSelect
                      size="small"
                      variant="standard"
                      label="Select Equipment"
                      options={equipmentOptions}
                      {...field}
                      onChange={handleChangeEquipment}
                    />
                  )}
                />

                <Box />
              </Box>

              <Box className="row">
                <InputField
                  label="Quantity"
                  control={control}
                  name="equipment.quantity"
                  inputProps={{ disabled: !isEditMode }}
                />

                <AutocompleteValueField<SelectOption, false, FinanceProgramFormValues>
                  control={control}
                  name="equipment.year"
                  disabled={!isEditMode}
                  options={equipmentYears}
                  textFieldProps={{ label: 'Year', size: 'small' }}
                  extraAction={nextValue => {
                    handleChangeSelectedProductsConfiguration!(FACT_NAMES_NUMERIC.EQUIPMENT_YEAR, nextValue);
                  }}
                />

                <SelectField
                  label="Make"
                  control={control}
                  name="equipment.make"
                  options={makeOptions}
                  helperText={dynamicFieldErrorText}
                  inputProps={{ disabled: !isEditMode }}
                  extraAction={nextValue => {
                    handleChangeSelectedProductsConfiguration!(FACT_NAMES_STRING.EQUIPMENT_MAKE, nextValue.toString());
                  }}
                />

                <InputField
                  label="Model"
                  control={control}
                  name="equipment.model"
                  inputProps={{ disabled: !isEditMode }}
                />

                <SelectField
                  label="Class"
                  control={control}
                  name="equipment.class"
                  options={classOptions}
                  inputProps={{ disabled: !isEditMode }}
                  error={classOptions.length ? determineDynamicFieldError(FACT_NAMES_STRING.EQUIPMENT_CLASS) : false}
                  extraAction={nextValue => {
                    handleChangeSelectedProductsConfiguration!(FACT_NAMES_STRING.EQUIPMENT_CLASS, nextValue.toString());
                  }}
                />

                <SerialNumberVinInput
                  handleSearchByVinResult={handleSearchByVinResult}
                  isSearchIconHidded={!isEditMode}
                  inputFieldProps={{
                    inputProps: {
                      disabled: !isEditMode,
                      required: formFieldsRequire?.['equipment.serialNumberVin']?.value,
                      helperText: formFieldsRequire?.['equipment.serialNumberVin']?.message ?? dynamicFieldErrorText,
                      error:
                        formFieldsRequire?.['equipment.serialNumberVin']?.value &&
                        determineDynamicFieldError(FACT_NAMES_STRING.VIN),
                    },
                    extraAction: {
                      debounceTime: 50,
                      cb: (nextValue: string) => {
                        handleChangeSelectedProductsConfiguration!(FACT_NAMES_STRING.VIN, nextValue);
                      },
                    },
                  }}
                />
              </Box>

              <Box className="row">
                <InputField
                  label="Stock Id"
                  control={control}
                  name="equipment.stockId"
                  inputProps={{ disabled: !isEditMode }}
                />

                <SelectField
                  label="Condition"
                  control={control}
                  name="equipment.newUsed"
                  options={equipmentConditions}
                  helperText={dynamicFieldErrorText}
                  inputProps={{ disabled: !isEditMode }}
                  extraAction={nextValue => {
                    handleChangeSelectedProductsConfiguration!(
                      FACT_NAMES_STRING.EQUIPMENT_CONDITION,
                      nextValue.toString()
                    );
                  }}
                />

                <InputField
                  label="Mileage"
                  control={control}
                  name="equipment.mileage"
                  inputProps={{
                    disabled: !isEditMode,
                    required: formFieldsRequire?.['equipment.mileage']?.value,
                    helperText: formFieldsRequire?.['equipment.mileage']?.message ?? dynamicFieldErrorText,
                    error:
                      formFieldsRequire?.['equipment.mileage']?.value &&
                      determineDynamicFieldError(FACT_NAMES_NUMERIC.ODOMETER_MILEAGE),
                  }}
                  formatter={value => equipmentMileageFormatter(value, 'toSimpleStr')}
                  extraAction={{
                    debounceTime: 50,
                    cb: nextValue => {
                      handleChangeSelectedProductsConfiguration!(
                        FACT_NAMES_NUMERIC.ODOMETER_MILEAGE,
                        nextValue.toString()
                      );
                    },
                  }}
                />

                <InputField
                  control={control}
                  label="Description"
                  name="equipment.description"
                  inputProps={{ disabled: !isEditMode }}
                />

                <CurrencyInputField
                  control={control}
                  label="Wholesale Cost"
                  name="equipment.totalWholesaleCost"
                  textFieldProps={{ disabled: !isEditMode }}
                />

                <CurrencyInputField
                  label="Retail Price"
                  control={control}
                  name="equipment.retailPrice"
                  textFieldProps={{ disabled: !isEditMode }}
                  customOnChange={(nextValue, name) => {
                    setValue(name, nextValue);
                    handleChangeSelectedProductsConfiguration!(FACT_NAMES_NUMERIC.DEAL_SIZE, nextValue);
                  }}
                />
              </Box>

              <ProductsDynamicFields onChangeAllProductsConfig={handleChangeSelectedProductsConfiguration!} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
